import { useMemo } from "react"
import { Row, Col, Label, Input } from "reactstrap"
import Flatpickr from "react-flatpickr"
import Select from "react-select"
import VehicleIdSelect from "components/Common/VehicleIdSelect"

const MONTHS = [
  { label: "January", value: 1 },
  { label: "February", value: 2 },
  { label: "March", value: 3 },
  { label: "April", value: 4 },
  { label: "May", value: 5 },
  { label: "June", value: 6 },
  { label: "July", value: 7 },
  { label: "August", value: 8 },
  { label: "September", value: 9 },
  { label: "October", value: 10 },
  { label: "November", value: 11 },
  { label: "December", value: 12 },
]

const currentYear = new Date().getFullYear()
const YEARS = Array.from({ length: 6 }, (_, i) => ({
  label: `${currentYear - i}`,
  value: currentYear - i,
}))

const pad = n => n.toString().padStart(2, "0")

const formatDate = date => {
  const d = new Date(date)
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

// Resolve a year/month selection into a date_from/date_to range.
const buildRange = (year, month) => {
  if (!year) {
    return { date_from: "", date_to: "" }
  }
  const y = Number(year)
  if (month) {
    const m = Number(month)
    const lastDay = new Date(y, m, 0).getDate()
    return {
      date_from: `${y}-${pad(m)}-01`,
      date_to: `${y}-${pad(m)}-${pad(lastDay)}`,
    }
  }
  return { date_from: `${y}-01-01`, date_to: `${y}-12-31` }
}

// Monday of a given ISO week/year.
const getMondayOfISOWeek = (week, year) => {
  const simple = new Date(year, 0, 1 + (week - 1) * 7)
  const dow = simple.getDay()
  const monday = new Date(simple)
  if (dow <= 4) {
    monday.setDate(simple.getDate() - simple.getDay() + 1)
  } else {
    monday.setDate(simple.getDate() + 8 - simple.getDay())
  }
  return monday
}

// Resolve an ISO week string ("2026-W21") into a Mon-Sun range.
const buildWeekRange = week => {
  if (!week || !week.includes("-W")) {
    return { date_from: "", date_to: "" }
  }
  const [y, w] = week.split("-W").map(Number)
  const monday = getMondayOfISOWeek(w, y)
  const sunday = new Date(monday)
  sunday.setDate(monday.getDate() + 6)
  return { date_from: formatDate(monday), date_to: formatDate(sunday) }
}

const VehicleFuelUsageFilter = ({
  filters,
  setFilters,
  userOptions = [],
  loading,
}) => {
  // Manual range picker — clears the year/month/week shortcuts.
  const handleDateChange = selectedDates => {
    setFilters(prev => ({
      ...prev,
      year: "",
      month: "",
      week: "",
      date_from: selectedDates[0] ? formatDate(selectedDates[0]) : "",
      date_to: selectedDates[1] ? formatDate(selectedDates[1]) : "",
    }))
  }

  const handleYearChange = selected => {
    const year = selected ? selected.value : ""
    setFilters(prev => ({
      ...prev,
      year,
      week: "",
      ...buildRange(year, prev.month),
    }))
  }

  const handleMonthChange = selected => {
    const month = selected ? selected.value : ""
    setFilters(prev => {
      const year = prev.year || (month ? currentYear : "")
      return {
        ...prev,
        year,
        month,
        week: "",
        ...buildRange(year, month),
      }
    })
  }

  const handleWeekChange = e => {
    const week = e.target.value
    setFilters(prev => ({
      ...prev,
      week,
      year: "",
      month: "",
      ...buildWeekRange(week),
    }))
  }

  const optionFormatter = rowData =>
    rowData?.map(item => ({
      label: item.name,
      value: item.id,
    })) || []

  const userSelectOptions = optionFormatter(userOptions)

  // Minimal formik-shaped adapter so the shared VehicleIdSelect component can
  // drive the `vehicle_id` filter without a real formik instance.
  const vehicleFilterFormik = useMemo(
    () => ({
      values: { vehicle_id: filters.vehicle_id || null },
      touched: {},
      errors: {},
      setFieldValue: (_field, value) =>
        setFilters(prev => ({ ...prev, vehicle_id: value || "" })),
    }),
    [filters.vehicle_id, setFilters]
  )

  return (
    <div className="border p-3 rounded bg-light">
      <div className="d-flex align-items-center gap-2 mb-3">
        <i className="bx bx-filter-alt text-primary fs-5" />
        <h6 className="mb-0 fw-semibold">Filter Fuel Usage</h6>
      </div>

      <Row className="g-3">
        <Col md={4}>
          <VehicleIdSelect formik={vehicleFilterFormik} />
        </Col>

        <Col md={4}>
          <Label>Fuel User</Label>
          <Select
            options={userSelectOptions}
            value={
              userSelectOptions.find(
                o => o.value === Number(filters.uses_by_id)
              ) || null
            }
            onChange={selected =>
              setFilters(prev => ({
                ...prev,
                uses_by_id: selected ? selected.value : "",
              }))
            }
            isClearable
            isLoading={loading}
            placeholder="Select a user"
          />
        </Col>

        <Col md={4}>
          <Label>Date Range</Label>
          <Flatpickr
            value={[filters.date_from, filters.date_to]}
            onChange={handleDateChange}
            options={{ mode: "range", dateFormat: "Y-m-d" }}
            className="form-control"
            placeholder="Select date range"
          />
        </Col>

        <Col md={4}>
          <Label>Year</Label>
          <Select
            options={YEARS}
            value={YEARS.find(o => o.value === Number(filters.year)) || null}
            onChange={handleYearChange}
            isClearable
            placeholder="Select year"
          />
        </Col>

        <Col md={4}>
          <Label>Month</Label>
          <Select
            options={MONTHS}
            value={MONTHS.find(o => o.value === Number(filters.month)) || null}
            onChange={handleMonthChange}
            isClearable
            placeholder="Select month"
          />
        </Col>

        <Col md={4}>
          <Label>Week</Label>
          <Input
            type="week"
            value={filters.week || ""}
            onChange={handleWeekChange}
          />
        </Col>
      </Row>
    </div>
  )
}

export default VehicleFuelUsageFilter
